// ═════════════════════════════════════════════════════════════════════
//  Settings selectors — pure helpers over the settings seed + legacy
//  users, mirroring SettingsController / User-Role relations.
// ═════════════════════════════════════════════════════════════════════

import { usersSeed, rolesSeed, storesSeed } from './settingsData.js';

export const CURRENT_USER_KEY = 'bursaku_current_user';

export function getSettingsData() {
  return {
    users: usersSeed,
    roles: rolesSeed,
    stores: storesSeed,
  };
}

export function getRoles(data = getSettingsData()) {
  return data.roles ?? rolesSeed;
}

export function getStore(data = getSettingsData()) {
  return (data.stores ?? storesSeed)[0] ?? null;
}

function roleIndex(data) {
  const map = new Map();
  for (const role of getRoles(data)) map.set(role.role_id, role);
  return map;
}

export function getUsers(data = getSettingsData()) {
  const byId = roleIndex(data);
  return (data.users ?? []).map((user) => ({
    ...user,
    roleName: byId.get(user.role)?.role_name ?? 'Unknown',
    roleType: byId.get(user.role)?.role_type ?? 'unknown',
  }));
}

export function getActiveSuperCount(data = getSettingsData()) {
  return getUsers(data).filter((u) => u.roleType === 'super').length;
}

export function getSoleSuperId(data = getSettingsData()) {
  if (getActiveSuperCount(data) !== 1) return null;
  const sole = getUsers(data).find((u) => u.roleType === 'super');
  return sole ? sole.id : null;
}

export function resolveCurrentUser(username) {
  const users = getUsers();
  const name = String(username ?? '').trim().toLowerCase();
  const match = users.find((u) => String(u.username).toLowerCase() === name);
  return match ?? users.find((u) => u.id === 1) ?? users[0];
}

function readCurrentUserUsername() {
  try {
    const stored = window.localStorage.getItem(CURRENT_USER_KEY);
    return stored === null ? '' : stored;
  } catch {
    return '';
  }
}

export function getCurrentUser() {
  return resolveCurrentUser(readCurrentUserUsername());
}

/**
 * Normalize the backend user payload (AuthController formatUserPayload) to
 * the seed-shaped object the UI consumes (id/username/name/email + roleName/
 * roleType/store/permissions).
 */
export function normalizeAuthUser(payload) {
  const role = payload?.role ?? null;
  return {
    id: payload?.id ?? null,
    store_id: payload?.store?.store_id ?? null,
    username: payload?.username ?? '',
    name: payload?.name ?? '',
    email: payload?.email ?? '',
    profile_picture: payload?.profile_picture ?? null,
    role,
    roleName: role?.role_name ?? 'Unknown',
    roleType: String(role?.role_type ?? 'unknown').toLowerCase(),
    store: payload?.store ?? null,
    permissions: payload?.permissions ?? [],
  };
}

/**
 * Current logged-in user from the real API session (localStorage.auth_user),
 * falling back to the demo seed user when no API session is stored.
 */
export function getAuthenticatedUser() {
  try {
    const raw = window.localStorage.getItem('auth_user');
    if (raw) {
      return normalizeAuthUser(JSON.parse(raw));
    }
  } catch {
    // fall through to the demo seed user
  }
  return getCurrentUser();
}

/**
 * Super administrator check for real API roles. The backend stores the role
 * type as either 'super' (existing data) or 'superadmin' (seeder).
 */
export function isSuperAdminRole(roleType) {
  const role = String(roleType ?? '').toLowerCase();
  return role === 'super' || role === 'superadmin';
}

/**
 * DevToolbar gate — the debug bar only appears for the super admin and
 * debugger roles (role-type based, no username carve-outs).
 */
export function canAccessDevToolbar(username) {
  const user = resolveCurrentUser(username);
  const role = String(user?.roleType ?? '').toLowerCase();
  return role === 'super' || role === 'superadmin' || role === 'debugger';
}

/**
 * Mirrors the legacy SettingsController guards:
 *  - cannot act on your own account
 *  - cannot demote/delete the last active super admin
 */
export function canManageUser(user, currentUser, soleSuperId) {
  if (!user || !currentUser) return false;
  if (user.id === currentUser.id) return false;
  if (isSuperAdminRole(user.roleType) && soleSuperId === user.id) return false;
  return true;
}

export function canChangeRole(user, currentUser, soleSuperId, newRoleType) {
  if (!canManageUser(user, currentUser, soleSuperId)) return false;
  if (isSuperAdminRole(user.roleType) && !isSuperAdminRole(newRoleType) && soleSuperId === user.id) return false;
  return true;
}
